import { Color, Spacing } from '@persona-id/design-tokens';
import { EditIcon } from '@persona-id/design-tokens/icons';
import { useState } from 'react';
import styled from 'styled-components';

import FieldsTable from 'dashboard/components/FieldsTable';
import Permissioned from 'dashboard/components/Permissioned';
import RelationsTable from 'dashboard/components/RelationsTable';
import { SimulatedDataSticker } from 'dashboard/components/SimulatedDataSticker/SimulatedDataSticker';
import FormAccountIdProperty, {
  AccountIdPropertyFormData,
} from 'dashboard/forms/FormAccountIdProperty';
import FormNewAccountIdentificationProperty from 'dashboard/forms/FormNewAccountIdentificationProperty';
import {
  formatAddressFieldDisplay,
  formatDate,
  formatNormalizedPostalCode,
} from 'dashboard/lib/utils/formatters';
import { Account } from 'dashboard/models/account';
import { EnvironmentType } from 'dashboard/models/environment';
import { AccountState } from 'dashboard/redux/slices/account';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import { FontWeight } from 'design-system/variables/text';
import { GOVERNMENT_ID_DISPLAY_STRINGS, GovernmentIdClass } from 'lib/constants/governmentIds';
import { CountryCode } from 'lib/constants/locales';
import { Field, FieldArray, FieldHash, FieldString } from 'lib/fields';
import { ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

const LEGACY_USER_ACCOUNT_TYPE_FIELDS = [
  'name',
  'address',
  'identification_numbers',
  'birthdate',
  'email_address',
  'phone_number',
];

const AttributeGrid = styled('div')`
  display: grid;
  grid-auto-flow: row;
`;

const StyledContainer = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr;
  font-weight: ${FontWeight.Regular};
  color: ${Color.CoreSlate1000};
  margin-right: ${Spacing[7]};
`;

const Bordered = styled('div')`
  border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};
  font-weight: ${FontWeight.Bold};
  margin-bottom: ${Spacing[4]};
`;

const BorderedWithButton = styled('div')`
  border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};
  font-weight: ${FontWeight.Bold};
  margin-bottom: ${Spacing[4]};
  display: grid;
  align-items: center;
  grid-template-columns: 1fr 40px;
  padding: ${Spacing[2]} 0;
`;

const StyledParagraph = styled(Paragraph)`
  margin-bottom: ${Spacing[2]};
`;

interface AccountProfileSectionProps {
  account: Account;
  relations: AccountState['relations'];
  environmentType: EnvironmentType;
  handleCreateIdNumber: (values: {
    'identification-number': string;
    'identification-class': string;
    'country-code': string;
  }) => Promise<void>;
  handleUpdateAccountIdNumbers: (values: AccountIdPropertyFormData, index: number) => Promise<void>;
}

const AccountProfileSection = ({
  account,
  relations,
  environmentType,
  handleUpdateAccountIdNumbers,
  handleCreateIdNumber,
}: AccountProfileSectionProps) => {
  const [editingIdNumberIndex, setEditingIdNumberIndex] = useState(null);
  const [showCreateIdentificationNumberForm, setShowCreateIdentificationNumberForm] =
    useState(false);

  const normalizeAddress = (
    addressStreet1: string,
    addressStreet2: string,
    addressCity: string,
    addressSubdivision: string,
    addressPostalCode: string,
    countryCode: string,
  ) => {
    const normalizedPostalCode = formatNormalizedPostalCode({
      addressPostalCode: addressPostalCode,
      addressCountryCode: countryCode,
    });

    return formatAddressFieldDisplay({
      addressStreet1: addressStreet1,
      addressStreet2: addressStreet2,
      addressCity: addressCity,
      addressSubdivision: addressSubdivision,
      addressPostalCode: normalizedPostalCode,
    });
  };

  const handleUpdateAccountSubmit = async (values: {
    'identification-number': string;
    'identification-class': string;
    'country-code': string;
  }) => {
    await handleCreateIdNumber(values);
    setShowCreateIdentificationNumberForm(false);
  };

  const customFields: Record<string, Field> = account.attributes.fields
    ? { ...account.attributes.fields }
    : {};
  for (const key of LEGACY_USER_ACCOUNT_TYPE_FIELDS) {
    delete customFields[key];
  }

  const fieldMapKeys = Object.keys(account.attributes['fields']);
  const showLegacyUserAccountTypeFields =
    fieldMapKeys.includes('selfie_photo') ||
    fieldMapKeys.includes('name') ||
    fieldMapKeys.includes('birthdate') ||
    fieldMapKeys.includes('address') ||
    fieldMapKeys.includes('email_address') ||
    fieldMapKeys.includes('phone_number');

  const fieldName = account.attributes.fields.name as unknown as FieldHash<{
    first: FieldString;
    middle: FieldString;
    last: FieldString;
  }>;
  const fieldAddress = account.attributes.fields.address as unknown as FieldHash<{
    street_1: FieldString;
    street_2: FieldString;
    subdivision: FieldString;
    city: FieldString;
    postal_code: FieldString;
    country_code: FieldString;
  }>;

  return (
    <>
      {showLegacyUserAccountTypeFields && (
        <Card data-test='account-profile-default-fields-section-card'>
          <Card.Body>
            <Card.Title>
              Account Information
              {environmentType === ApiModel.EnvironmentSandbox && (
                <SimulatedDataSticker dataSectionLabel='account-profile-section' />
              )}
            </Card.Title>
            <AttributeGrid>
              <Bordered>Basic</Bordered>
              <AttributeGrid>
                <StyledContainer>
                  <StyledParagraph>Name</StyledParagraph>
                  <StyledParagraph>
                    {fieldName &&
                      [
                        fieldName.value.first.value,
                        fieldName.value.middle.value,
                        fieldName.value.last.value,
                      ].join(' ')}
                  </StyledParagraph>
                </StyledContainer>

                <StyledContainer>
                  <StyledParagraph>Birthdate</StyledParagraph>
                  <StyledParagraph>
                    {formatDate(account.attributes.fields.birthdate?.value as string)}
                  </StyledParagraph>
                </StyledContainer>
                <StyledContainer>
                  <StyledParagraph>Address</StyledParagraph>
                  <StyledParagraph>
                    {fieldAddress &&
                      normalizeAddress(
                        fieldAddress.value.street_1.value,
                        fieldAddress.value.street_2.value,
                        fieldAddress.value.city.value,
                        fieldAddress.value.subdivision.value,
                        fieldAddress.value.postal_code.value,
                        fieldAddress.value.country_code.value,
                      )}
                  </StyledParagraph>
                </StyledContainer>
              </AttributeGrid>
              <Spacer />

              <Bordered>Contact</Bordered>
              <AttributeGrid>
                <StyledContainer>
                  <StyledParagraph>Email</StyledParagraph>
                  <StyledParagraph>
                    {account.attributes.fields.email_address?.value as string}
                  </StyledParagraph>
                </StyledContainer>
                <StyledContainer>
                  <StyledParagraph>Phone</StyledParagraph>
                  <StyledParagraph>
                    {account.attributes.fields.phone_number?.value as string}
                  </StyledParagraph>
                </StyledContainer>
              </AttributeGrid>
              <Spacer />

              <BorderedWithButton>
                <span>Identification Numbers</span>
                <Permissioned permission={Permission.AccountWrite}>
                  <Button
                    onClick={() => {
                      setShowCreateIdentificationNumberForm(true);
                      setEditingIdNumberIndex(null);
                    }}
                    variant='basic'
                    width='auto'
                  >
                    Add
                  </Button>
                </Permissioned>
              </BorderedWithButton>

              <AttributeGrid>
                {showCreateIdentificationNumberForm && (
                  <>
                    <FormNewAccountIdentificationProperty
                      handleCancel={() => setShowCreateIdentificationNumberForm(false)}
                      onSubmit={handleUpdateAccountSubmit}
                    />
                    <Spacer height={2} />
                  </>
                )}

                {(account.attributes.fields['identification_numbers'] as FieldArray)?.value?.map(
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  (idNumber: FieldHash, index) => {
                    return (
                      <InlineIDPropertyEdit
                        key={index}
                        idNumber={idNumber}
                        idNumberIndex={index}
                        showTitle={true}
                        isEditing={editingIdNumberIndex === index}
                        handleEditButtonClick={() => {
                          setShowCreateIdentificationNumberForm(false);
                          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                          setEditingIdNumberIndex(index);
                        }}
                        handleFormCancel={() => setEditingIdNumberIndex(null)}
                        handleUpdateAccountIdNumbers={handleUpdateAccountIdNumbers}
                      />
                    );
                  },
                )}
              </AttributeGrid>
            </AttributeGrid>
          </Card.Body>
        </Card>
      )}
      {Object.entries(customFields).length ? (
        <Card data-test='account-profile-custom-fields-section-card'>
          <Card.Body>
            <Card.Title>Fields</Card.Title>
            <FieldsTable fields={customFields} />
          </Card.Body>
        </Card>
      ) : undefined}
      {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
      {Object.keys(relations).length > 0 && (
        <Card>
          <Card.Body>
            <Card.Title>Relations</Card.Title>
            {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
            <RelationsTable relations={relations} />
          </Card.Body>
        </Card>
      )}
    </>
  );
};

export default AccountProfileSection;

const InlinePropertyEditRow = styled.div<{ isHovered: boolean }>`
  display: grid;
  align-items: center;
  grid-template-columns: 1fr 40px;
  height: 48px;
  background-color: ${({ isHovered }) => (isHovered ? Color.CorePurple100 : Color.PrimaryWhite)};
`;

const InlinePropertyInfoContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
`;

const EditButton = styled(Button)<{ isHovered: boolean }>`
  visibility: ${({ isHovered }) => (isHovered ? 'visible' : 'hidden')};
  background-color: white;
  height: 24px;
  width: 24px;
`;

const StyledIDPropertyTitle = styled.div`
  font-weight: ${FontWeight.Regular};
  color: ${Color.CoreSlate1000};
`;
interface InlineIDPropertyEditProps {
  idNumber: FieldHash;
  idNumberIndex: number;
  showTitle: boolean;
  isEditing: boolean;
  handleEditButtonClick: () => void;
  handleFormCancel: () => void;
  handleUpdateAccountIdNumbers: (values: AccountIdPropertyFormData, index: number) => Promise<void>;
}

const InlineIDPropertyEdit = ({
  idNumber,
  idNumberIndex,
  showTitle,
  isEditing,
  handleEditButtonClick,
  handleFormCancel,
  handleUpdateAccountIdNumbers,
}: InlineIDPropertyEditProps) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = () => {
    handleEditButtonClick();
    setIsHovered(false);
  };

  const issuingCountry = idNumber.value.issuing_country.value as CountryCode;
  const identificationClass = idNumber.value.identification_class.value as string;
  const identificationNumber = idNumber.value.identification_number.value as string;

  const handleFormAccountIdPropertySubmit = async (values: AccountIdPropertyFormData) => {
    await handleUpdateAccountIdNumbers(
      {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'issuing-country': issuingCountry,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'identification-class': identificationClass,
        ...values,
      },
      idNumberIndex,
    );

    handleFormCancel();
  };

  if (isEditing) {
    return (
      <>
        <FormAccountIdProperty
          onSubmit={handleFormAccountIdPropertySubmit}
          handleCancel={handleFormCancel}
          initialValues={{
            'identification-number': identificationNumber,
          }}
        />
        <Spacer height={2} />
      </>
    );
  } else {
    const propertyTitle =
      idNumber.value['identification_class'].value === 'ssn'
        ? 'Social Security Number'
        : GOVERNMENT_ID_DISPLAY_STRINGS[
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            idNumber.value['identification_class'].value as GovernmentIdClass
          ];
    return (
      <InlinePropertyEditRow
        isHovered={isHovered}
        key={idNumberIndex}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <InlinePropertyInfoContainer>
          <StyledIDPropertyTitle>{showTitle ? propertyTitle : ''}</StyledIDPropertyTitle>
          <StyledParagraph data-test='account-profile-section__identification-number'>
            {identificationNumber}
          </StyledParagraph>
        </InlinePropertyInfoContainer>
        <Permissioned permission={Permission.AccountWrite}>
          <EditButton
            isHovered={isHovered}
            variant='basic'
            size='sm'
            width='icon'
            onClick={handleClick}
          >
            <EditIcon />
          </EditButton>
        </Permissioned>
      </InlinePropertyEditRow>
    );
  }
};

const FieldDisplay = ({
  indent,
  fieldKey,
  field,
}: {
  indent: number;
  fieldKey?: string;
  field: Field;
}) => {
  let prefix = '';
  let newIndent = 0;
  if (fieldKey) {
    prefix = `${' '.repeat(indent * 2)}${fieldKey}: `;
    newIndent = indent + 1;
  } else {
    prefix = '';
    newIndent = indent;
  }
  if (field.type === 'array') {
    return (
      <pre>
        {prefix ? <pre>{prefix}</pre> : undefined}
        {field.value.map((subField, idx) => {
          return (
            <FieldDisplay key={idx} indent={newIndent} fieldKey={idx.toString()} field={subField} />
          );
        })}
      </pre>
    );
  } else if (field.type === 'hash') {
    return (
      <pre>
        {prefix ? <pre>{prefix}</pre> : undefined}
        {Object.entries(field.value).map(([subKey, subField]) => {
          return (
            <FieldDisplay key={subKey} indent={newIndent} fieldKey={subKey} field={subField} />
          );
        })}
      </pre>
    );
  } else if (['string', 'number', 'date'].includes(field.type)) {
    return (
      <pre>
        {prefix}
        {String(field.value)}
      </pre>
    );
  } else {
    return (
      <pre>
        {prefix}
        {JSON.stringify(field.value, undefined, 2)}
      </pre>
    );
  }
};
